import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ConfiguredOrderFiling, OrderDraftInput, OrderSnapshot, OrderSummary } from '@technohull/shared';
import { api } from '../lib/api.js';

export interface CatalogueItem {
  id: number;
  category_id: number;
  code: string;
  name: string;
  description: string | null;
  is_standard: number;
  default_quantity: number;
  input_type: string;
  has_drawing: number;
  internal_price: number | null;
  dealer_price: number | null;
  customer_price: number | null;
  display_order: number;
}
export interface CatalogueCategory {
  id: number;
  name: string;
  kind: string;
  discountEligible: boolean;
  display_order: number;
  items: CatalogueItem[];
}
export interface CatalogueResponse {
  models: { id: number; code: string; name: string; year: number | null }[];
  model: number | null;
  categories: CatalogueCategory[];
}

export function useCatalogue(modelId?: number) {
  return useQuery({
    queryKey: ['catalogue', modelId ?? 'default'],
    queryFn: () => api.get<CatalogueResponse>(`/api/orders/catalogue${modelId ? `?modelId=${modelId}` : ''}`),
    staleTime: 10 * 60_000,
  });
}

export function useOrders() {
  return useQuery({
    queryKey: ['orders'],
    queryFn: () => api.get<{ orders: OrderSummary[] }>('/api/orders'),
  });
}

export interface ChangeEvent {
  changeType: string;
  entity: string;
  field?: string;
  oldValue?: string | null;
  newValue?: string | null;
}
export interface RequirementRow {
  id: number;
  seq: number;
  category: string | null;
  description: string;
  source: string | null;
  acknowledged_by: string | null;
  acknowledged_at: string | null;
  completed_by: string | null;
  completed_at: string | null;
}
/** the model's included-by-default equipment, sent with the order so the assembly export can
 *  pre-tick the standard checkboxes without a second catalogue fetch. */
export interface StandardEquip {
  equipmentId: number;
  name: string;
  category?: string | null;
}

export interface OrderDetail {
  order: OrderSummary;
  snapshot: OrderSnapshot;
  revisions: Array<{
    id: number;
    revision_number: number;
    revision_type: string;
    created_by: string;
    created_at: string;
    confirmation_note: string | null;
    change_summary: string | null;
  }>;
  requirements: RequirementRow[];
  locked: boolean;
  categoryOrder: string[];
  standardEquipment?: StandardEquip[];
}
export interface ProdOrderSummary extends OrderSummary {
  fileName: string;
  requirementCount: number;
  /** requirements the floor hasn't marked done */
  openRequirementCount: number;
  productionLocked: boolean;
  completion: number | null;
}

/** after Assembly acts on an order (accept, production, open / lock for Sales): every list and the order */
const afterAssembly = (qc: ReturnType<typeof useQueryClient>, id: number) => {
  void qc.invalidateQueries({ queryKey: ['production-orders'] });
  void qc.invalidateQueries({ queryKey: ['orders'] });
  void qc.invalidateQueries({ queryKey: ['order', id] });
  void qc.invalidateQueries({ queryKey: ['edit-lock', id] });
};
export interface SpecField {
  code: string;
  label_en: string;
  label_gr: string | null;
  section: string | null;
  display_order: number;
}
export function useProductionOrders() {
  return useQuery({
    queryKey: ['production-orders'],
    queryFn: () =>
      api.get<{ orders: ProdOrderSummary[]; specFields: SpecField[]; categoryOrder: string[] }>('/api/production-orders'),
  });
}
/** put an order in production (Assembly's hard lock) or take it out */
export function useLockOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: number; locked: boolean }) => api.post('/api/orders/lock', v),
    onSuccess: (_d, v) => afterAssembly(qc, v.id),
  });
}
/** Assembly accepts the order's latest revision (the one they reviewed) */
export function useAcceptOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: number; revision: number }) => api.post<{ acceptedRevision: number }>('/api/orders/accept', v),
    onSuccess: (_d, v) => afterAssembly(qc, v.id),
  });
}
export function useRequirementAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: number; action: 'ack' | 'unack' | 'complete' | 'incomplete'; orderId: number }) =>
      api.post('/api/orders/requirement', { id: v.id, action: v.action }),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ['order', v.orderId] }),
  });
}
export function useExportLog() {
  return useMutation({
    mutationFn: (v: { id: number; revisionId: number; filename: string; exportType?: string }) =>
      api.post('/api/orders/export-log', v),
  });
}
export function useOrder(id: number | null) {
  return useQuery({
    queryKey: ['order', id],
    queryFn: () => api.get<OrderDetail>(`/api/orders/get?id=${id}`),
    enabled: !!id,
  });
}

export function useCreateDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (draft: OrderDraftInput) => api.post<{ id: number; orderNumber: string }>('/api/orders', { draft }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orders'] }),
  });
}
export function useUpdateDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: number; draft: OrderDraftInput }) => api.post('/api/orders/update', v),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['order', v.id] });
    },
  });
}
/** what confirming or revising an order did on the boat's order file: filed (the Order Sheet row and the order
 *  file record) or why it wasn't */
export interface OrderFiled {
  orderFile?: ConfiguredOrderFiling;
  orderFileError?: string;
}
const afterFiling = (qc: ReturnType<typeof useQueryClient>, id: number) => {
  void qc.invalidateQueries({ queryKey: ['orders'] });
  void qc.invalidateQueries({ queryKey: ['production-orders'] });
  void qc.invalidateQueries({ queryKey: ['order', id] });
  void qc.invalidateQueries({ queryKey: ['order-sheet'] });
  void qc.invalidateQueries({ queryKey: ['order-files'] });
};
export function useConfirmOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: number; note?: string }) =>
      api.post<{ revisionNumber: number } & OrderFiled>('/api/orders/confirm', v),
    onSuccess: (_d, v) => afterFiling(qc, v.id),
  });
}
export function useEditOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: number; draft: OrderDraftInput; note?: string }) =>
      api.post<{ revisionNumber: number; changes: number } & OrderFiled>('/api/orders/edit', v),
    onSuccess: (_d, v) => afterFiling(qc, v.id),
  });
}
/** file a committed order again on its boat's Order Sheet row and order file */
export function useFileOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<{ orderFile: ConfiguredOrderFiling }>('/api/orders/file', { id }).then((r) => r.orderFile),
    onSuccess: (_d, id) => afterFiling(qc, id),
  });
}
export function useDeleteOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post('/api/orders/delete', { id }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['orders'] }); void qc.invalidateQueries({ queryKey: ['production-orders'] }); },
  });
}
export function useRestoreOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post('/api/orders/restore', { id }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['orders'] }); void qc.invalidateQueries({ queryKey: ['production-orders'] }); },
  });
}

// ── sales edit-lock workflow ─────────────────────────────────────────────────────
export interface EditLockState {
  lock: {
    is_unlocked_for_sales: number;
    unlocked_by: string | null;
    unlock_reason: string | null;
    unlock_instructions: string | null;
    expires_at: string | null;
  } | null;
  unlockedForSales: boolean;
}
export function useEditLock(id: number | null) {
  return useQuery({
    queryKey: ['edit-lock', id],
    queryFn: () => api.get<EditLockState>(`/api/orders/edit-lock?id=${id}`),
    enabled: !!id,
  });
}
export function useUnlockOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: number; reason: string; instructions?: string; expiresInHours?: number }) => api.post('/api/orders/unlock', v),
    onSuccess: (_d, v) => afterAssembly(qc, v.id),
  });
}
export function useRelockOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: number; reason?: string }) => api.post('/api/orders/relock', v),
    onSuccess: (_d, v) => afterAssembly(qc, v.id),
  });
}

/** Identity-only edit (Customer / vessel name / SAP) — allowed even when the order is locked. */
export function useQuickEditIdentity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: number; customer?: string; vesselName?: string; sapNumber?: string }) =>
      api.post<{ changes: string[] } & OrderFiled>('/api/orders/edit-identity', v),
    onSuccess: (_d, v) => {
      // a confirmed order is filed again under its new SAP / name
      afterFiling(qc, v.id);
    },
  });
}
